import React, { useEffect, useMemo, useRef, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { useTranslation } from "react-i18next";

import TableListPagination, { TablePaginationOptions } from "../../../components/TableListPagination";
import { useLayoutMode } from "../../../hooks/useLayoutMode";

export type WorkflowColumn<T> = TableColumn<T> & { name?: React.ReactNode };

interface Props<T> {
    columns: WorkflowColumn<T>[];
    data: T[];
    /** Server-side paging; omit for a list that is loaded whole. */
    paging?: { page: number; perPage: number; total: number; onPage: (page: number) => void };
    onRowClick?: (row: T) => void;
    dense?: boolean;
}

// The same look as components/TableList: transparent rows, the theme's hover tint.
const styles = (dark: boolean) => ({
    table: { style: { backgroundColor: 'transparent' } },
    headRow: { style: { backgroundColor: 'transparent' } },
    rows: {
        style: { backgroundColor: 'transparent' },
        highlightOnHoverStyle: {
            backgroundColor: dark ? 'rgba(54, 197, 241,.1)' : 'rgba(54, 197, 241,.05)',
            transitionDuration: '0.15s',
            transitionProperty: 'background-color',
        },
    },
    pagination: { style: { backgroundColor: 'transparent' } },
});

/**
 * The automation lists' table: the app's list styling (bold 14px headings, 14px cells, the shared
 * pagination footer) without TableList's coupling to the Redux list state and URL hash.
 */
function WorkflowTable<T>({ columns, data, paging, onRowClick, dense = false }: Props<T>) {
    const { t } = useTranslation(['common']);
    const layoutMode = useLayoutMode();

    const styled = useMemo(() => columns.map((column) => ({
        ...column,
        name: typeof column.name === 'string' ? <span className="font-weight-bold fs-14">{column.name}</span> : column.name,
        conditionalCellStyles: [{ when: () => true, classNames: dense ? ['fs-13'] : ['fs-14', 'p-2', 'lh-md'] }],
    })), [columns, dense]);

    // react-data-table keeps its own page once paging starts; go back to page 1 with the parent.
    const [reset, setReset] = useState(false);
    const lastPage = useRef(paging?.page);
    useEffect(() => {
        if (paging && paging.page === 1 && lastPage.current !== 1) {
            setReset((r) => !r);
        }
        lastPage.current = paging?.page;
    }, [paging]);

    const paginationOptions: TablePaginationOptions = {
        rowsPerPageText: t('common:rowsPerPage'),
        rangeSeparatorText: t('common:rangeSeparator'),
        selectAllRowsItem: false,
        isShowChangePageSize: false,
    };

    return (
        <DataTable
            columns={styled as TableColumn<T>[]}
            data={data}
            theme={layoutMode}
            customStyles={styles(layoutMode === 'dark')}
            persistTableHead
            responsive
            highlightOnHover
            pointerOnHover={!!onRowClick}
            onRowClicked={onRowClick ? (row) => onRowClick(row) : undefined}
            dense={dense}
            noDataComponent={<div className="emptySet pt-3">{t('common:emptyDataSet')}</div>}
            pagination={!!paging}
            paginationServer
            paginationPerPage={paging?.perPage}
            paginationTotalRows={paging?.total}
            paginationDefaultPage={paging?.page}
            paginationResetDefaultPage={reset}
            onChangePage={(page) => paging?.onPage(page)}
            paginationComponent={TableListPagination as any}
            paginationComponentOptions={paginationOptions}
        />
    );
}

export default WorkflowTable;
